import {useCurrentTime} from '@hconnect/common/hooks'
import {KpiType} from '@hconnect/common/types'
import {
  getArrayUrlParam,
  getUrlParam,
  setOrDeleteArrayParam,
  setOrDeleteStringParam
} from '@hconnect/common/utils'
import {Moment} from 'moment-timezone'
import {useState} from 'react'
import {useSearchParams} from 'react-router'

const PerformanceReviewPageParams = {
  KPI_TYPE: 'kpiType',
  COMMENTS_KPI_TYPES: 'commentsKpiTypes'
}

const DEFAULT_KPI_TYPE = KpiType.ClinkerProduction

type PerformanceReviewPageOptions = {
  timezone: string
}

export const usePerformanceReviewPageState = ({timezone}: PerformanceReviewPageOptions) => {
  const plantViewState = usePlantViewState()
  const selectedDateState = usePerformanceReviewSelectedDateState(timezone)
  const commentsKpiTypesState = useCommentsKpiTypesState()

  return {...plantViewState, ...selectedDateState, ...commentsKpiTypesState}
}

const usePerformanceReviewSelectedDateState = (timezone: string) => {
  const [selectedDate, setSelectedDate] = useState<Moment | undefined>()
  const currentMonth = useCurrentTime(timezone, 'month')
  const previousMonth = currentMonth.clone().subtract(1, 'month')

  return {
    onSelectedDateChange: setSelectedDate,
    selectedDate: selectedDate ?? previousMonth
  }
}

const usePlantViewState = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const kpiTypeParam = getUrlParam(searchParams, PerformanceReviewPageParams.KPI_TYPE)
  const kpiType = isKpiType(kpiTypeParam) ? kpiTypeParam : DEFAULT_KPI_TYPE

  const setKpiType = (value?: KpiType) => {
    const newParams = new URLSearchParams(searchParams)

    setOrDeleteStringParam(newParams, PerformanceReviewPageParams.KPI_TYPE, value)
    setSearchParams(newParams)
  }

  return {kpiType, setKpiType}
}

const useCommentsKpiTypesState = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const kpiTypesParam = getArrayUrlParam<KpiType>(
    searchParams,
    PerformanceReviewPageParams.COMMENTS_KPI_TYPES
  )
  const commentsKpiTypes = kpiTypesParam?.filter(isKpiType) ?? []

  const setCommentsKpiTypes = (value?: KpiType[]) => {
    const newParams = new URLSearchParams(searchParams)

    setOrDeleteArrayParam(newParams, PerformanceReviewPageParams.COMMENTS_KPI_TYPES, value)
    setSearchParams(newParams)
  }

  return {commentsKpiTypes, setCommentsKpiTypes}
}

const isKpiType = (value: string | undefined): value is KpiType =>
  Object.values(KpiType).includes(value as KpiType)
